import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { MessageCircle, ArrowRight, Phone, Building2, ChevronRight, CheckCircle2, AlertTriangle, Target, Layers, BarChart3, Users, Sparkles, HelpCircle } from 'lucide-react';
import { ChatWidget } from '@/components/ChatWidget';
import MegaFooter from '@/components/MegaFooter';
import Navbar from '@/components/Navbar';
import SEOHead from '@/components/SEOHead';
import PlatformArchitecture from '@/components/PlatformArchitecture';
import RelatedSolutions from '@/components/RelatedSolutions';
import ChannelProgrammeModule from '@/components/ChannelProgrammeModule';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function cleanBrandText(value) {
  return String(value || '')
    .replace(/tagn\s*pay\.ai/gi, 'channelloyalty.ai')
    .replace(/tagn\s*pay/gi, 'ChannelLoyalty');
}

function humanizeKey(key) {
  return String(key || '')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, function(letter) { return letter.toUpperCase(); });
}

function structuredContentToMarkdown(value, depth) {
  var level = depth || 0;
  if (typeof value === 'string') {
    var text = value.trim();
    if ((text.startsWith('{') && text.endsWith('}')) || (text.startsWith('[') && text.endsWith(']'))) {
      try { return structuredContentToMarkdown(JSON.parse(text), level); } catch (error) { return cleanBrandText(text); }
    }
    return cleanBrandText(text);
  }
  if (Array.isArray(value)) {
    return value.map(function(item) {
      var rendered = structuredContentToMarkdown(item, level + 1);
      return typeof item === 'object' && item !== null ? rendered : '- ' + rendered;
    }).filter(Boolean).join('\n\n');
  }
  if (value && typeof value === 'object') {
    return Object.keys(value).map(function(key) {
      var rendered = structuredContentToMarkdown(value[key], level + 1);
      if (!rendered) return '';
      if (/^(point|gap|item|step|pillar)_?\d+$/i.test(key)) return '- ' + rendered;
      return '**' + humanizeKey(key) + ':** ' + rendered;
    }).filter(Boolean).join('\n\n');
  }
  return value === undefined || value === null ? '' : cleanBrandText(value);
}

const WhatsAppFloat = () => (
  <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="whatsapp-float">
    <MessageCircle size={28} color="white" />
  </a>
);

function StickyCTA() {
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 py-3 z-40 shadow-lg" data-testid="sticky-cta">
      <div className="container flex items-center justify-between">
        <div className="hidden md:block">
          <span className="text-sm font-semibold text-gray-900">Ready to transform your channel loyalty?</span>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto justify-center md:justify-end">
          <Link to="/contact" className="btn btn-primary text-sm px-6" data-testid="sticky-book-demo">
            Book Demo <ArrowRight size={16} />
          </Link>
          <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="btn btn-whatsapp text-sm px-6">
            <MessageCircle size={16} /> Talk to Expert
          </a>
        </div>
      </div>
    </div>
  );
}

function InlineCTA({ variant }) {
  if (variant === 'proposal') {
    return (
      <div className="my-10 bg-gradient-to-r from-[#2C39B8] to-pink-600 rounded-2xl p-8 text-white" data-testid="inline-cta-proposal">
        <div className="max-w-2xl">
          <h3 className="text-xl font-bold mb-2">Get a Customized Loyalty Solution for Your Industry</h3>
          <p className="text-white/80 text-sm mb-4">Our channel loyalty experts will design a tailored program architecture, reward structure, and ROI projection for your specific business context.</p>
          <div className="flex flex-wrap gap-3">
            <Link to="/contact" className="bg-white text-[#2C39B8] px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-gray-100 transition-colors inline-flex items-center gap-2">
              Request Proposal <ArrowRight size={16} />
            </Link>
            <a href="https://wa.me/919910059861" target="_blank" rel="noopener noreferrer" className="border border-white/40 text-white px-6 py-2.5 rounded-lg font-semibold text-sm hover:bg-white/10 transition-colors inline-flex items-center gap-2">
              <Phone size={16} /> +91 9910059861
            </a>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="my-10 bg-gray-900 rounded-2xl p-8 text-white" data-testid="inline-cta-demo">
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
        <div>
          <h3 className="text-lg font-bold mb-1">See ChannelLoyalty in Action</h3>
          <p className="text-gray-400 text-sm">15-minute personalized demo with a channel loyalty specialist.</p>
        </div>
        <div className="flex gap-3 flex-shrink-0">
          <Link to="/contact" className="btn btn-primary text-sm">
            Book Demo <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function LeadCaptureForm() {
  const [form, setForm] = useState({ name: '', company: '', industry: '', phone: '' });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setLoading(true);
    try {
      await fetch(API_URL + '/api/leads', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      setSubmitted(true);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  if (submitted) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-2xl p-8 text-center" data-testid="lead-form-success">
        <CheckCircle2 size={48} className="text-green-600 mx-auto mb-4" />
        <h3 className="text-xl font-bold text-gray-900 mb-2">Request Received</h3>
        <p className="text-gray-600">Our channel loyalty team will reach out within 24 hours with a tailored proposal.</p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-2xl p-8" data-testid="lead-capture-form">
      <h3 className="text-xl font-bold text-gray-900 mb-1">Request a Customized Proposal</h3>
      <p className="text-gray-600 text-sm mb-6">Our loyalty architects will design a program blueprint tailored to your industry and channel structure.</p>
      <form onSubmit={handleSubmit} className="grid md:grid-cols-2 gap-4">
        <input type="text" placeholder="Full Name" required value={form.name} onChange={function(e) { setForm({...form, name: e.target.value}); }} className="px-4 py-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none" data-testid="lead-name" />
        <input type="text" placeholder="Company Name" required value={form.company} onChange={function(e) { setForm({...form, company: e.target.value}); }} className="px-4 py-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none" data-testid="lead-company" />
        <select value={form.industry} onChange={function(e) { setForm({...form, industry: e.target.value}); }} className="px-4 py-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none text-gray-700" data-testid="lead-industry">
          <option value="">Select Industry</option>
          <option value="Cement">Cement & Building Materials</option>
          <option value="Paints">Paints & Coatings</option>
          <option value="Steel">Steel & Metals</option>
          <option value="FMCG">FMCG</option>
          <option value="Pharma">Pharmaceuticals</option>
          <option value="Auto Parts">Auto Parts & Lubricants</option>
          <option value="Electrical">Electrical & Electronics</option>
          <option value="Pipes">Pipes & Sanitaryware</option>
          <option value="Petroleum">Petroleum & Energy</option>
          <option value="Fertilizers">Fertilizers & Agri</option>
          <option value="Other">Other Manufacturing</option>
        </select>
        <input type="tel" placeholder="Phone / WhatsApp Number" required value={form.phone} onChange={function(e) { setForm({...form, phone: e.target.value}); }} className="px-4 py-3 rounded-lg border border-gray-300 text-sm focus:ring-2 focus:ring-blue-700 focus:border-transparent outline-none" data-testid="lead-phone" />
        <div className="md:col-span-2">
          <button type="submit" disabled={loading} className="btn btn-primary w-full justify-center" data-testid="lead-submit">
            {loading ? 'Submitting...' : 'Get Custom Proposal'} <ArrowRight size={16} />
          </button>
        </div>
      </form>
    </div>
  );
}

function FAQSection({ faqs }) {
  const [openIdx, setOpenIdx] = useState(null);

  if (!faqs || faqs.length === 0) return null;

  return (
    <div className="my-12" data-testid="faq-section">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
      <div className="space-y-3">
        {faqs.map(function(faq, idx) {
          var isOpen = openIdx === idx;
          return (
            <div key={idx} className="border border-gray-200 rounded-xl overflow-hidden">
              <button onClick={function() { setOpenIdx(isOpen ? null : idx); }} className="w-full text-left px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors">
                <span className="font-semibold text-gray-900 text-sm pr-4">{faq.question}</span>
                <ChevronRight size={18} className={"text-gray-400 flex-shrink-0 transition-transform " + (isOpen ? "rotate-90" : "")} />
              </button>
              {isOpen && (
                <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-100 pt-3">
                  {faq.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {/* FAQ Schema for SEO */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faqs.map(function(faq) {
          return {
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": {
              "@type": "Answer",
              "text": faq.answer
            }
          };
        })
      })}} />
    </div>
  );
}

function RelatedPages({ pages, currentSlug }) {
  var filtered = pages.filter(function(p) { return p.slug !== currentSlug; }).slice(0, 6);
  if (filtered.length === 0) return null;

  return (
    <div className="my-12 bg-gray-50 rounded-2xl p-8" data-testid="related-pages">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-900">Related Solutions</h3>
        <Link to="/solutions" className="text-xs font-medium text-blue-600 hover:text-[#2C39B8] flex items-center gap-1">
          View All Solutions <ArrowRight size={12} />
        </Link>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map(function(page) {
          return (
            <Link key={page.slug} to={"/solutions/" + page.slug} className="block p-4 bg-white rounded-xl border border-gray-200 hover:shadow-md hover:border-blue-200 transition-all">
              <div className="text-xs text-[#2C39B8] font-medium mb-1">{page.tag || page.industry || 'B2B Loyalty'}</div>
              <h4 className="text-sm font-bold text-gray-900 line-clamp-2">{page.title}</h4>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

export default function SEOPage() {
  var params = useParams();
  var slug = params.slug;
  var [page, setPage] = useState(null);
  var [related, setRelated] = useState([]);
  var [loading, setLoading] = useState(true);
  var [notFound, setNotFound] = useState(false);

  useEffect(function() {
    async function fetchPage() {
      setLoading(true);
      try {
        var res = await fetch(API_URL + '/api/seo/pages/' + slug);
        if (!res.ok) { setNotFound(true); setLoading(false); return; }
        var data = await res.json();
        setPage(data);

        // Fetch related pages (smart — same industry/stakeholder)
        var relRes = await fetch(API_URL + '/api/seo/related/' + slug);
        var relData = await relRes.json();
        setRelated(relData.related || []);
      } catch (err) {
        setNotFound(true);
      }
      setLoading(false);
    }
    fetchPage();
  }, [slug]);

  if (loading) {
    return (
      <div data-testid="seo-page-loading">
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div>
        </div>
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div data-testid="seo-page-not-found">
        <SEOHead title="Page Not Found" description="The page you're looking for doesn't exist. Explore 500+ channel loyalty solutions." noindex={true} />
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Page Not Found</h1>
            <p className="text-gray-600 mb-6">The page you're looking for doesn't exist.</p>
            <div className="flex gap-4 justify-center">
              <Link to="/solutions" className="btn btn-primary" data-testid="notfound-solutions-link">Browse All Solutions</Link>
              <Link to="/" className="btn btn-secondary" data-testid="notfound-home-link">Back to Home</Link>
            </div>
          </div>
        </div>
        <MegaFooter />
      </div>
    );
  }

  // Parse structured content sections
  var rawSections = page.sections || {};
  var faqs = page.faqs || [];
  var contentMarkdown = structuredContentToMarkdown(page.content);

  // Normalize sections: convert arrays/objects to markdown strings
  var sections = {};
  Object.keys(rawSections).forEach(function(key) {
    var val = rawSections[key];
    sections[key] = structuredContentToMarkdown(val);
  });

  // Build JSON-LD structured data
  var articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": page.title,
    "description": page.meta_description,
    "url": "https://channelloyalty.ai/solutions/" + slug,
    "datePublished": page.created_at || new Date().toISOString(),
    "dateModified": page.updated_at || page.created_at || new Date().toISOString(),
    "author": {"@type": "Organization", "name": "ChannelLoyalty"},
    "publisher": {
      "@type": "Organization",
      "name": "ChannelLoyalty",
      "url": "https://channelloyalty.ai",
      "logo": {"@type": "ImageObject", "url": "https://channelloyalty.ai/logo.png"}
    },
    "keywords": (page.keywords || []).join(", "),
    "mainEntityOfPage": {"@type": "WebPage", "@id": "https://channelloyalty.ai/solutions/" + slug}
  };

  var breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {"@type": "ListItem", "position": 1, "name": "Home", "item": "https://channelloyalty.ai"},
      {"@type": "ListItem", "position": 2, "name": "Solutions", "item": "https://channelloyalty.ai/solutions"},
      {"@type": "ListItem", "position": 3, "name": page.title, "item": "https://channelloyalty.ai/solutions/" + slug}
    ]
  };

  return (
    <div data-testid="seo-page">
      <Navbar />
      <SEOHead 
        title={page.title}
        description={page.meta_description}
        keywords={page.keywords ? page.keywords.join(', ') : ''}
        canonical={'https://channelloyalty.ai/solutions/' + slug}
        ogType="article"
      />
      
      {/* Article + Breadcrumb JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
      
      {/* Hero Section */}
      <section className="hero-full" style={{ paddingBottom: '48px' }}>
        <div className="container">
          <div className="max-w-4xl pt-6">
            <nav className="flex items-center gap-2 text-sm text-white/60 mb-6" data-testid="breadcrumb">
              <Link to="/" className="hover:text-white">Home</Link>
              <ChevronRight size={14} />
              <Link to="/solutions" className="hover:text-white">Solutions</Link>
              <ChevronRight size={14} />
              <span className="text-white/90">{page.industry || 'B2B Loyalty'}</span>
            </nav>
            <h1 className="text-white text-4xl md:text-5xl font-bold mb-4 leading-tight" data-testid="seo-page-title">
              {page.h1 || page.title}
            </h1>
            <p className="text-white/80 text-lg leading-relaxed mb-6">
              {page.meta_description}
            </p>
            <div className="flex flex-wrap gap-3">
              {page.industry && (
                <span className="bg-white/10 text-white text-xs px-3 py-1.5 rounded-full font-medium">{page.industry}</span>
              )}
              {page.stakeholder && (
                <span className="bg-white/10 text-white text-xs px-3 py-1.5 rounded-full font-medium">{page.stakeholder}</span>
              )}
            </div>
          </div>
        </div>
      </section>

      <main className="pb-20">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            
            {/* Authority Introduction */}
            {sections.introduction && (
              <div className="my-10">
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.introduction}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* CTA After Intro */}
            <InlineCTA variant="demo" />

            {/* Industry Problem */}
            {(sections.industry_problem || sections.the_challenge) && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <AlertTriangle size={20} className="text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">The Industry Challenge</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.industry_problem || sections.the_challenge}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Current Gaps */}
            {sections.current_gaps && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Target size={20} className="text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Gaps in Existing Solutions</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.current_gaps}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Strategic Framework */}
            {(sections.framework || sections.strategic_framework) && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Layers size={20} className="text-[#2C39B8]" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Strategic Framework</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.framework || sections.strategic_framework}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Interactive Platform Architecture */}
            <PlatformArchitecture pageTitle={page.title} />

            {/* Mid-Content CTA */}
            <InlineCTA variant="proposal" />

            {/* ChannelLoyalty Solution */}
            {(sections.channelloyalty_solution || sections.the_channelloyalty_approach) && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Sparkles size={20} className="text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">The ChannelLoyalty Approach</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.channelloyalty_solution || sections.the_channelloyalty_approach}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Use Case */}
            {(sections.use_case || sections.case_context) && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <BarChart3 size={20} className="text-blue-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Industry Use Case</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.use_case || sections.case_context}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Comparison (if exists) */}
            {(sections.comparison || sections.comparison_table) && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                    <Users size={20} className="text-cyan-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Competitive Comparison</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.comparison || sections.comparison_table}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Implementation Roadmap (paint narrative pages) */}
            {sections.implementation_roadmap && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <Target size={20} className="text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Implementation Roadmap</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.implementation_roadmap}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Metrics That Matter (paint narrative pages) */}
            {sections.metrics_that_matter && (
              <div className="my-12">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                    <BarChart3 size={20} className="text-amber-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900">Metrics That Matter</h2>
                </div>
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections.metrics_that_matter}</ReactMarkdown>
                </div>
              </div>
            )}

            {/* Generic renderer — silo/keyword pages with custom section keys */}
            {(function() {
              var RENDERED_KEYS = {
                introduction: 1, industry_problem: 1, the_challenge: 1, current_gaps: 1,
                framework: 1, strategic_framework: 1, channelloyalty_solution: 1, the_channelloyalty_approach: 1,
                use_case: 1, case_context: 1, comparison: 1, comparison_table: 1,
                implementation_roadmap: 1, metrics_that_matter: 1
              };
              var extraKeys = Object.keys(sections).filter(function(k) { return !RENDERED_KEYS[k] && sections[k]; });
              if (extraKeys.length === 0) return null;
              var blocks = extraKeys.map(function(k) {
                var heading = k.replace(/_/g, ' ').replace(/\b\w/g, function(ch) { return ch.toUpperCase(); });
                return (
                  <div className="my-12" key={k} data-testid={"section-" + k}>
                    <h2 className="text-2xl font-bold text-gray-900 mb-4">{heading}</h2>
                    <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{sections[k]}</ReactMarkdown>
                    </div>
                  </div>
                );
              });
              return <div>{blocks}</div>;
            })()}

            {slug === 'channel-loyalty-program' && <ChannelProgrammeModule />}

            {/* Fallback: render raw content if no structured sections */}
            {!sections.introduction && !sections.executive_insight && contentMarkdown && (
              <div className="my-10">
                <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentMarkdown}</ReactMarkdown>
                </div>
                <InlineCTA variant="proposal" />
              </div>
            )}

            {/* FAQ Section */}
            <FAQSection faqs={faqs} />

            {/* Lead Capture Form */}
            <LeadCaptureForm />

            {/* Related Pages (Internal Linking) */}
            <RelatedPages pages={related} currentSlug={slug} />
          </div>
        </div>
      </main>

      <MegaFooter />
      <StickyCTA />
      <WhatsAppFloat />
      <ChatWidget />
    </div>
  );
}
